import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { pickI18n } from '../i18n'
import { timeAgo } from '../lib.js'
import { PlayIcon } from './Icons.jsx'

const catNames = (clip) => clip.cats.map((c) => c.name).join(', ')

/** Clip preview; `onOpen(clip)` shows the player. */
export function ClipCard({ clip, onOpen, showFeeder = true }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const cats = catNames(clip)
  return (
    <button
      type="button"
      onClick={() => onOpen(clip)}
      aria-label={t('cabinet.playClip')}
      className="group flex cursor-pointer flex-col gap-2 text-left"
    >
      <span className="relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-2xl bg-slate">
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
        ) : null}
        <span className="relative flex size-12 items-center justify-center rounded-full bg-white/90 text-[#1e2a35] transition group-hover:scale-110">
          <PlayIcon size={20} />
        </span>
        {clip.mine || clip.followed_cat ? (
          <span className="absolute top-2.5 left-2.5 rounded-full bg-teal px-2.5 py-1 text-xs font-extrabold text-on-teal">
            {clip.mine ? t('cabinet.yourMeal') : `♥ ${cats}`}
          </span>
        ) : null}
        <span className="absolute right-2.5 bottom-2.5 rounded-full bg-black/55 px-2 py-0.5 text-xs font-bold text-white">
          0:{String(Math.round(clip.duration)).padStart(2, '0')}
        </span>
      </span>
      <span className="flex justify-between gap-2 text-[15px]">
        <span className="truncate font-extrabold text-ink">
          {cats || (showFeeder ? pickI18n(clip.feeder.name, lang) : t(`cabinet.kind.${clip.kind}`))}
        </span>
        <span className="shrink-0 font-bold text-subtle">{timeAgo(clip.ts, lang)}</span>
      </span>
      {showFeeder && cats ? <span className="-mt-1.5 truncate text-sm text-subtle">{pickI18n(clip.feeder.name, lang)}</span> : null}
    </button>
  )
}

/** Full-screen player in a native <dialog> (focus trap, Escape, backdrop for free). */
export function ClipModal({ clipId, onClose }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const ref = useRef(null)
  const { data: clip, error } = useApi(() => api.clip(clipId), [clipId])

  useEffect(() => {
    const dialog = ref.current
    if (dialog && !dialog.open) dialog.showModal()
  }, [])

  const share = async () => {
    const url = `${window.location.origin}/feeders/${clip.feeder.slug}?clip=${clip.id}`
    if (navigator.share) await navigator.share({ title: 'UrbanPurr', url }).catch(() => {})
    else await navigator.clipboard?.writeText(url)
  }

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && ref.current.close()}
      className="m-auto w-[min(960px,calc(100vw-32px))] max-w-none rounded-3xl bg-black p-0 text-white backdrop:bg-black/70"
    >
      <div className="flex items-center justify-between gap-4 px-5 py-3.5">
        <span className="truncate font-extrabold">
          {clip ? (clip.mine ? t('cabinet.yourMeal') : catNames(clip) || t(`cabinet.kind.${clip.kind}`)) : ''}
        </span>
        <span className="flex gap-2">
          {clip ? (
            <button type="button" onClick={share} className="cursor-pointer rounded-full px-3 py-1.5 font-bold hover:bg-white/15">
              {t('cabinet.share')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => ref.current.close()}
            aria-label={t('cabinet.close')}
            className="cursor-pointer rounded-full px-3 py-1.5 text-lg font-black hover:bg-white/15"
          >
            ✕
          </button>
        </span>
      </div>
      {clip ? (
        <>
          <video src={clip.video_url} poster={clip.poster_url ?? undefined} controls autoPlay muted loop playsInline className="block aspect-video w-full bg-black" />
          <p className="m-0 px-5 py-3.5 text-[15px] font-bold text-[#c5ced6]">
            <Link to={`/feeders/${clip.feeder.slug}`} className="text-inherit no-underline hover:text-white" onClick={() => ref.current.close()}>
              {pickI18n(clip.feeder.name, lang)} · {timeAgo(clip.ts, lang)} ›
            </Link>
          </p>
        </>
      ) : (
        <p className="m-0 px-5 py-16 text-center font-bold">
          {error ? t(error.status === 404 ? 'cabinet.clipGone' : 'common.error') : t('common.loading')}
        </p>
      )}
    </dialog>
  )
}

/** Current title, meal count and the way to the next one. */
export function RankCard({ profile }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { feed_count: count = 0, rank, next_rank: next } = profile
  const from = rank?.min_feedings ?? 0
  const progress = next ? Math.min(100, ((count - from) / (next.min_feedings - from)) * 100) : 100
  return (
    <Link to="/ranks" className="flex flex-col gap-3.5 rounded-3xl bg-mint p-6 text-ink no-underline transition hover:opacity-95">
      <span className="flex items-center gap-4">
        <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-surface text-[34px]">{rank?.emoji ?? '🐱'}</span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-bold text-subtle">{rank ? t('cabinet.level', { level: rank.level }) : t('cabinet.noRankYet')}</span>
          <span className="truncate text-2xl font-black">{rank ? pickI18n(rank.name, lang) : t('cabinet.newcomer')}</span>
        </span>
        <span className="flex flex-col items-end">
          <span className="text-[28px] leading-none font-black text-teal">{count}</span>
          <span className="text-[13px] font-bold text-subtle">{t('cabinet.meals', { count })}</span>
        </span>
      </span>
      <span
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        className="block h-2.5 overflow-hidden rounded-full bg-track"
      >
        <span className="block h-full rounded-full bg-teal-soft" style={{ width: `${progress}%` }} />
      </span>
      {profile.donated_meals ? (
        <span className="text-sm text-subtle">
          {t('cabinet.split', { fed: profile.fed_count, donated: profile.donated_meals })}
        </span>
      ) : null}
      <span className="text-[15px] text-muted">
        {next ? t('cabinet.toNext', { count: next.min_feedings - count, rank: `${next.emoji} ${pickI18n(next.name, lang)}` }) : t('cabinet.topRank')}
      </span>
    </Link>
  )
}
